import { useNavigate, useParams } from "react-router-dom";
import { useEvent } from "../../lib/events";
import { Modal } from "../ui/Modal";
import {
  EventView,
  composeEventEyebrow,
  type EventBasePath,
  type NewsBasePath,
} from "./EventView";

/**
 * Modal-route element for `/calendar/:slug` and `/dashboard/calendar/:slug`.
 * Mounted by the secondary <Routes> block in App.tsx when a backgroundLocation
 * is present — i.e. the user clicked a list item and we want to overlay the
 * detail rather than swap to a full page.
 *
 * `onClose` calls navigate(-1) so the URL reverts to the prior list location;
 * if there's no history (rare — e.g. somehow opened directly with state), we
 * fall back to navigate(basePath).
 */

interface EventModalRouteProps {
  basePath: EventBasePath;
  newsBasePath: NewsBasePath;
}

export function EventModalRoute({ basePath, newsBasePath }: EventModalRouteProps) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const eventQ = useEvent(slug);
  const ev = eventQ.data?.event;

  const onClose = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
    } else {
      navigate(basePath, { replace: true });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={ev?.title ?? "Event"}
      eyebrow={ev ? composeEventEyebrow(ev) : undefined}
      size="lg"
    >
      {slug && (
        <EventView
          slug={slug}
          mode="modal"
          basePath={basePath}
          newsBasePath={newsBasePath}
        />
      )}
    </Modal>
  );
}
